/**
 * EmailUtils.js
 * Utility functions and constants for email handling
 */

/**
 * Selectors for email quotes from various email clients
 */
export const QUOTES_SELECTORS = [
  '.gmail_quote',
  '[class*="yahoo_quoted"]',
  '.protonmail_quote',
  '.gmail_extra',
  '.moz-cite-prefix',
  '.ss-email-thread__quote',
  '#isForwardContent',
  '#isReplyContent',
  '#origbody',
  'blockquote[type="cite"]',
  '#appendonsend ~ *',
];

/**
 * CSS styles for hiding/showing email quotes
 */
export const EMAIL_QUOTES_STYLES = `
.email-body .gmail_quote,
.email-body [class*="yahoo_quoted"],
.email-body .protonmail_quote,
.email-body .gmail_extra,
.email-body .moz-cite-prefix,
.email-body #isForwardContent,
.email-body #isReplyContent,
.email-body #origbody,
.email-body blockquote[type="cite"],
.email-body #appendonsend ~ *,
.email-body .ss-email-thread__quote {
  display: none !important;
}

.email-body.expanded .gmail_quote,
.email-body.expanded [class*="yahoo_quoted"],
.email-body.expanded .protonmail_quote,
.email-body.expanded .gmail_extra,
.email-body.expanded .moz-cite-prefix,
.email-body.expanded #isForwardContent,
.email-body.expanded #isReplyContent,
.email-body.expanded #origbody,
.email-body.expanded blockquote[type="cite"],
.email-body.expanded #appendonsend ~ *,
.email-body.expanded .ss-email-thread__quote {
  display: revert !important;
}
`;

/**
 * Check if an HTML element contains email quotes
 * @param {HTMLElement} element - The DOM element to check
 * @returns {boolean} - True if quotes exist in the element
 */
export const checkEmailQuotesExist = (element) => {
  if (!element) return false;

  return QUOTES_SELECTORS.some((selector) => element.querySelector(selector));
};

/**
 * Cache for processed email bodies
 * @type {Map<string, string>}
 */
const emailBodyCache = new Map();

/**
 * Clear email body cache (exported for testing)
 */
export const clearEmailBodyCache = () => emailBodyCache.clear();

/**
 * Process HTML content to ensure all links open in new tabs
 * Optimized single-pass implementation
 * @param {string} html - Raw HTML content
 * @returns {string} - Processed HTML content
 */
export const processEmailBody = (html) => {
  if (!html) return '';

  // Quick check if there are any links
  if (!html.includes('<a')) return html;

  // Use cache to avoid processing the same content multiple times
  if (emailBodyCache.has(html)) {
    return emailBodyCache.get(html);
  }

  let result = '';
  let i = 0;
  const len = html.length;

  while (i < len) {
    // Look for next <a tag
    const tagStart = html.indexOf('<a', i);

    if (tagStart === -1) {
      // No more tags, add the rest and break
      result += html.slice(i);
      break;
    }

    // Add everything up to the tag
    result += html.slice(i, tagStart);

    // Find the end of the tag
    const tagEnd = html.indexOf('>', tagStart);
    if (tagEnd === -1) {
      // Malformed HTML, just add the rest and break
      result += html.slice(tagStart);
      break;
    }

    // Get the tag content
    const tagContent = html.slice(tagStart, tagEnd);

    // Check if attributes already exist
    const hasTarget = tagContent.includes('target=');
    const hasRel = tagContent.includes('rel=');

    // Add the processed tag
    result += tagContent;
    if (!hasTarget) result += ' target="_blank"';
    if (!hasRel) result += ' rel="noopener noreferrer"';
    result += '>';

    // Move pointer after the tag
    i = tagEnd + 1;
  }

  // Cache management
  if (emailBodyCache.size > 1000) {
    emailBodyCache.clear();
  }
  emailBodyCache.set(html, result);

  return result;
};
